import {
  Box,
  type ChipProps,
  Chip as MuiChip,
  Stack,
  type Theme,
  alpha,
  chipClasses,
  styled,
} from "@mui/material"

const chipSizeConfig = {
  tiny: {
    padding: (theme: Theme) => "4px 10px",
    fontSize: (theme: Theme) => theme.typography.pxToRem(13),
    height: "24px",
    iconHeight: (theme: Theme) => theme.typography.pxToRem(14),
  },
  small: {
    padding: (theme: Theme) => `${theme.spacing(1)} ${theme.spacing(1.5)}`,
    fontSize: (theme: Theme) => theme.typography.pxToRem(14),
    height: "32px",
    iconHeight: (theme: Theme) => theme.typography.pxToRem(16),
  },
  medium: {
    // 16px16px
    padding: (theme: Theme) => theme.spacing(2),
    fontSize: (theme: Theme) => theme.typography.pxToRem(16),
    height: "48px",
    iconHeight: (theme: Theme) => theme.typography.pxToRem(19),
  },
}

export const StrikeThroughChipContainer = styled(MuiChip, {
  shouldForwardProp: (propName) =>
    !["mainColor", "withDarkBorder", "chipSize"].includes(
      typeof propName === "string" ? propName : "",
    ),
})<{
  mainColor: string
  withDarkBorder?: boolean
  chipSize?: keyof typeof chipSizeConfig
}>(({ theme, mainColor, withDarkBorder, chipSize = "medium" }) => {
  const config = chipSizeConfig[chipSize]

  return {
    position: "relative",
    overflow: "hidden",
    padding: config.padding(theme),
    borderColor: withDarkBorder ? mainColor : alpha(mainColor, 0.3),
    borderRadius: "100px",
    color: mainColor,
    fontSize: config.fontSize(theme),
    fontWeight: 500,
    height: config.height,
    [`.${chipClasses.icon}`]: {
      height: config.iconHeight(theme),
      color: mainColor,
    },
    [`.${chipClasses.label}`]: {
      padding: 0,
    },
  }
})
StrikeThroughChipContainer.defaultProps = { variant: "outlined" }

interface StrikeThroughChipProps extends Omit<ChipProps, "color" | "size"> {
  color: string
  isStrikethrough?: boolean
  underlineGap?: string | number
  size?: "tiny" | "small" | "medium"
  withDarkBorder?: boolean
  haveUnderline?: boolean
}

export const StrikeThroughChip = ({
  label,
  color,
  isStrikethrough,
  underlineGap = "inherit",
  size = "medium",
  withDarkBorder = false,
  haveUnderline = false,
  ...chipProps
}: StrikeThroughChipProps) => {
  return (
    <StrikeThroughChipContainer
      {...chipProps}
      mainColor={color}
      withDarkBorder={withDarkBorder || isStrikethrough}
      chipSize={size}
      label={
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          fontWeight={600}
          color={color}
          py={0.5}
          borderBottom={`${haveUnderline ? "1" : "0"}px solid ${alpha(color, 0.3)}`}
          lineHeight={underlineGap}
        >
          {label}
          {isStrikethrough && (
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              sx={{
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "0",
                  left: "0",
                  width: "100%",
                  height: "100%",
                  background: color,
                  clipPath:
                    "polygon(0% 100%, 1% 100%, 100% 1%, 100% 0%, 99% 0%, 0% 99%)",
                },
              }}
            />
          )}
        </Stack>
      }
    />
  )
}
